import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Button, Input, MessageBar, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import { ChevronLeft24Regular, ChevronRight24Regular } from "@fluentui/react-icons";
import { Text } from "@fluentui/react-components";

import "pdfjs-dist/build/pdf.worker.min.mjs";
import { PDFDocumentProxy } from "pdfjs-dist";
import { pdfDocument } from "../models/heretic/pdfDocument";
import { z } from "zod";
import { CustomXmlPart } from "../models/heretic/customXmlPart";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: any) => void;
    };
  }
}

if (typeof Promise.withResolvers === "undefined") {
  Promise.withResolvers = function <T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

if (typeof Promise.allSettled === "undefined") {
  Promise.allSettled = function (promises: Promise<any>[]) {
    return Promise.all(
      promises.map((promise) => {
        // Ensure the value is a Promise before calling .then
        const promiseValue = Promise.resolve(promise);
        return promiseValue
          .then((value) => ({ status: "fulfilled" as const, value }))
          .catch((reason) => ({ status: "rejected" as const, reason }));
      })
    );
  };
}

interface PDFViewerProps {
  url: string | null;
  initialPage?: number;
  sourceDocument?: CustomXmlPart["source_document"];
  fileName?: string;
  visible?: boolean;
  currentDocumentExtraction?: z.infer<typeof pdfDocument>;
  onSnippingComplete?: (snippedArea: {
    content?: string;
    bounding_box: [number, number][];
    pageNumber: number;
    page_width: number;
    page_height: number;
  }) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  initialPage = 1,
  sourceDocument,
  visible = true,
  currentDocumentExtraction,
  onSnippingComplete,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [canvasSize, setCanvasSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const lastUrlRef = useRef(url);
  const [isLoading, setIsLoading] = useState(false);

  // Snipping state
  const [isSnipping, setIsSnipping] = useState(false);
  const [snippingBox, setSnippingBox] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);

  const [highlightedWords, setHighlightedWords] = useState<
    Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>
  >([]);

  const scaledBoundingBox = React.useMemo(() => {
    if (!sourceDocument || !sourceDocument.bounding_box) {
      return null;
    }

    if (!canvasSize) {
      console.log("No canvas size");
      return null;
    }

    return {
      id: sourceDocument.id,
      x: sourceDocument.bounding_box[0][0] * (canvasSize.width / (sourceDocument.page_width ?? 1)),
      y: sourceDocument.bounding_box[0][1] * (canvasSize.height / (sourceDocument.page_height ?? 1)),
      width:
        (sourceDocument.bounding_box[1][0] - sourceDocument.bounding_box[0][0]) *
        (canvasSize.width / (sourceDocument.page_width ?? 1)),
      height:
        (sourceDocument.bounding_box[2][1] - sourceDocument.bounding_box[0][1]) *
        (canvasSize.height / (sourceDocument.page_height ?? 1)),
      pageNumber: sourceDocument.page_number,
    };
  }, [sourceDocument, canvasSize]);

  const highlightStyles = React.useMemo(() => {
    if (!scaledBoundingBox || scaledBoundingBox.pageNumber !== pageNumber) return null;

    // return react elements
    return (
      <div
        key={scaledBoundingBox.id}
        style={{
          position: "absolute" as const,
          top: `${scaledBoundingBox.y * (zoomLevel / 100)}px`,
          left: `${scaledBoundingBox.x * (zoomLevel / 100)}px`,
          width: `${scaledBoundingBox.width * (zoomLevel / 100)}px`,
          height: `${scaledBoundingBox.height * (zoomLevel / 100)}px`,
          border: "2px solid blue",
          backgroundColor: "rgba(0, 0, 255, 0.1)",
          pointerEvents: "none" as const,
          zIndex: 10,
        }}
      />
    );
  }, [scaledBoundingBox, zoomLevel, pageNumber]);

  // Calculate the current snipping box style
  const snippingBoxStyle = React.useMemo(() => {
    if (!snippingBox) return null;

    const left = Math.min(snippingBox.startX, snippingBox.currentX);
    const top = Math.min(snippingBox.startY, snippingBox.currentY);
    const width = Math.abs(snippingBox.currentX - snippingBox.startX);
    const height = Math.abs(snippingBox.currentY - snippingBox.startY);

    return {
      position: "absolute" as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
      border: "2px dashed blue",
      backgroundColor: "rgba(0, 0, 255, 0.1)",
      pointerEvents: "none" as const,
      zIndex: 20,
    };
  }, [snippingBox]);

  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        const newWidth = canvasRef.current.clientWidth;
        const newHeight = canvasRef.current.clientHeight;
        if (newWidth !== canvasSize?.width || newHeight !== canvasSize?.height) {
          setCanvasSize({ width: newWidth, height: newHeight });
        }
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const renderPage = useCallback(async () => {
    if (!pdf) return;

    try {
      const page = await pdf.getPage(pageNumber);
      const canvas = canvasRef.current;
      const container = containerRef.current;

      if (!canvas || !container) return;

      const context = canvas.getContext("2d");

      const containerWidth = container.clientWidth;
      const viewport = page.getViewport({ scale: 1 });
      const containerScale = containerWidth / viewport.width;
      const renderScale = containerScale * 3;

      const scaledViewport = page.getViewport({ scale: renderScale });
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      setCanvasSize({ width: containerWidth, height: scaledViewport.height * (containerScale / renderScale) });

      const renderContext = {
        canvasContext: context!,
        viewport: scaledViewport,
      };

      page.render(renderContext);
    } catch (error: unknown) {
      console.error("Error rendering PDF page:", error);
      if (error instanceof Error) {
        setError(`Failed to render PDF page: ${error.message}`);
      }
    }
  }, [pdf, pageNumber]);

  useEffect(() => {
    if (numPages && initialPage >= 1 && initialPage <= numPages) {
      setPageNumber(initialPage);
    }
  }, [initialPage, numPages]);

  useEffect(() => {
    // Reset PDF state when URL changes or becomes empty
    const urlChanged = url !== lastUrlRef.current;
    const urlBecameEmpty = !url && lastUrlRef.current;

    if (urlChanged || urlBecameEmpty) {
      console.log("URL changed or became empty, resetting PDF state", {
        previous: lastUrlRef.current,
        current: url,
      });

      setPdf(null);
      setNumPages(null);
      setError(null);
      setPageNumber(initialPage);
      setPdfLoaded(false);
      setIsLoading(false);

      lastUrlRef.current = url;
    }
  }, [url, initialPage]);

  useEffect(() => {
    const loadPDF = async () => {
      // Only attempt to load if:
      // 1. We have a non-empty URL
      // 2. Component is visible
      // 3. PDF hasn't been loaded yet
      // 4. We're not already loading
      if (!url || url === "" || !visible || pdfLoaded || isLoading) {
        return;
      }

      try {
        console.log("Loading PDF from URL:", url);
        setIsLoading(true);
        const loadingTask = pdfjsLib.getDocument(url);
        const loadedPdf = await loadingTask.promise;
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setPdfLoaded(true);
      } catch (error: unknown) {
        console.error("Error loading PDF:", error);
        if (error instanceof Error) {
          setError(`Failed to load PDF: ${error.message}`);
        }
      }
      setIsLoading(false);
    };

    loadPDF().then();
  }, [url, visible, pdfLoaded, isLoading]);

  // Effect to re-render when page number or zoom level changes
  useEffect(() => {
    if (visible && pdf) {
      renderPage().then();
    }
  }, [pageNumber, zoomLevel, renderPage, visible, pdf]);

  const changePage = (delta: number) => {
    setPageNumber((prevPageNumber) => {
      const newPageNumber = prevPageNumber + delta;
      return Math.max(1, Math.min(newPageNumber, numPages || 1));
    });
  };

  // Snipping handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;

    setIsSnipping(true);
    setSnippingBox({
      startX,
      startY,
      currentX: startX,
      currentY: startY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSnipping || !snippingBox || !canvasRef.current || !currentDocumentExtraction || !canvasSize) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    setSnippingBox({
      ...snippingBox,
      currentX,
      currentY,
    });

    // Calculate the current selection box in PDF coordinates
    const left = Math.min(snippingBox.startX, currentX);
    const top = Math.min(snippingBox.startY, currentY);
    const width = Math.abs(currentX - snippingBox.startX);
    const height = Math.abs(currentY - snippingBox.startY);

    const unzoomedX = left / (zoomLevel / 100);
    const unzoomedY = top / (zoomLevel / 100);
    const unzoomedWidth = width / (zoomLevel / 100);
    const unzoomedHeight = height / (zoomLevel / 100);

    const pageWidth = currentDocumentExtraction.widths[pageNumber - 1] || 1;
    const pageHeight = currentDocumentExtraction.heights[pageNumber - 1] || 1;

    const pdfX = unzoomedX / (canvasSize.width / pageWidth);
    const pdfY = unzoomedY / (canvasSize.height / pageHeight);
    const pdfWidth = unzoomedWidth / (canvasSize.width / pageWidth);
    const pdfHeight = unzoomedHeight / (canvasSize.height / pageHeight);

    const pageExtraction = currentDocumentExtraction.pages.filter((page) => page.pageNumber === pageNumber)[0];

    const intersectingWords = pageExtraction.words.filter((word) => {
      if (!word.boundingBox) return false;

      const [topLeft, topRight, bottomRight, bottomLeft] = word.boundingBox;

      const elementLeft = Math.min(topLeft[0], bottomLeft[0]);
      const elementRight = Math.max(topRight[0], bottomRight[0]);
      const elementTop = Math.min(topLeft[1], topRight[1]);
      const elementBottom = Math.max(bottomLeft[1], bottomRight[1]);

      // Calculate intersection area
      const intersectionLeft = Math.max(elementLeft, pdfX);
      const intersectionRight = Math.min(elementRight, pdfX + pdfWidth);
      const intersectionTop = Math.max(elementTop, pdfY);
      const intersectionBottom = Math.min(elementBottom, pdfY + pdfHeight);

      // Check if there's an intersection
      if (intersectionRight < intersectionLeft || intersectionBottom < intersectionTop) {
        return false;
      }

      // Calculate areas
      const intersectionArea = (intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop);
      const elementArea = (elementRight - elementLeft) * (elementBottom - elementTop);

      // Calculate intersection percentage
      const intersectionPercentage = intersectionArea / elementArea;

      // Only include elements with at least 40% intersection
      return intersectionPercentage >= 0.4;
    });

    // Convert PDF coordinates back to canvas coordinates for highlighting
    const highlightedWordsPositions = intersectingWords
      .map((word) => {
        if (!word.boundingBox) return null;
        const [topLeft, topRight, bottomRight, bottomLeft] = word.boundingBox;

        const elementLeft = Math.min(topLeft[0], bottomLeft[0]);
        const elementTop = Math.min(topLeft[1], topRight[1]);
        const elementWidth = Math.max(topRight[0], bottomRight[0]) - elementLeft;
        const elementHeight = Math.max(bottomLeft[1], bottomRight[1]) - elementTop;

        return {
          x: elementLeft * (canvasSize.width / pageWidth) * (zoomLevel / 100),
          y: elementTop * (canvasSize.height / pageHeight) * (zoomLevel / 100),
          width: elementWidth * (canvasSize.width / pageWidth) * (zoomLevel / 100),
          height: elementHeight * (canvasSize.height / pageHeight) * (zoomLevel / 100),
        };
      })
      .filter((pos): pos is NonNullable<typeof pos> => pos !== null);

    setHighlightedWords(highlightedWordsPositions);
  };

  const handleMouseUp = () => {
    if (!isSnipping || !snippingBox || !currentDocumentExtraction || !canvasSize) return;

    const left = Math.min(snippingBox.startX, snippingBox.currentX);
    const top = Math.min(snippingBox.startY, snippingBox.currentY);
    const width = Math.abs(snippingBox.currentX - snippingBox.startX);
    const height = Math.abs(snippingBox.currentY - snippingBox.startY);

    // Minimum size threshold (5x5 pixels)
    const MIN_SNIP_SIZE = 5;
    if (width < MIN_SNIP_SIZE || height < MIN_SNIP_SIZE) {
      setIsSnipping(false);
      setSnippingBox(null);
      setHighlightedWords([]);
      return;
    }

    const unzoomedX = left / (zoomLevel / 100);
    const unzoomedY = top / (zoomLevel / 100);
    const unzoomedWidth = width / (zoomLevel / 100);
    const unzoomedHeight = height / (zoomLevel / 100);

    const pageWidth = currentDocumentExtraction.widths[pageNumber - 1] || 1;
    const pageHeight = currentDocumentExtraction.heights[pageNumber - 1] || 1;

    const pdfX = unzoomedX / (canvasSize.width / pageWidth);
    const pdfY = unzoomedY / (canvasSize.height / pageHeight);
    const pdfWidth = unzoomedWidth / (canvasSize.width / pageWidth);
    const pdfHeight = unzoomedHeight / (canvasSize.height / pageHeight);

    let matchedContent = null;

    const pageExtraction = currentDocumentExtraction.pages.filter((page) => page.pageNumber === pageNumber)[0];

    const intersectingElements = pageExtraction.words.filter((word) => {
      if (!word.boundingBox) return false;

      const [topLeft, topRight, bottomRight, bottomLeft] = word.boundingBox;

      const elementLeft = Math.min(topLeft[0], bottomLeft[0]);
      const elementRight = Math.max(topRight[0], bottomRight[0]);
      const elementTop = Math.min(topLeft[1], topRight[1]);
      const elementBottom = Math.max(bottomLeft[1], bottomRight[1]);

      // Calculate intersection area
      const intersectionLeft = Math.max(elementLeft, pdfX);
      const intersectionRight = Math.min(elementRight, pdfX + pdfWidth);
      const intersectionTop = Math.max(elementTop, pdfY);
      const intersectionBottom = Math.min(elementBottom, pdfY + pdfHeight);

      // Check if there's an intersection
      if (intersectionRight < intersectionLeft || intersectionBottom < intersectionTop) {
        return false;
      }

      // Calculate areas
      const intersectionArea = (intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop);
      const elementArea = (elementRight - elementLeft) * (elementBottom - elementTop);

      // Calculate intersection percentage
      const intersectionPercentage = intersectionArea / elementArea;

      // Only include elements with at least 40% intersection
      return intersectionPercentage >= 0.4;
    });

    if (intersectingElements.length > 0) {
      matchedContent = intersectingElements.map((word) => word.stringContent).join(" ");
    }

    const bounding_box: [number, number][] = [
      [pdfX, pdfY], // top-left
      [pdfX + pdfWidth, pdfY], // top-right
      [pdfX + pdfWidth, pdfY + pdfHeight], // bottom-right
      [pdfX, pdfY + pdfHeight], // bottom-left
    ];

    if (onSnippingComplete) {
      onSnippingComplete({
        bounding_box,
        content: matchedContent,
        pageNumber,
        page_width: pageWidth,
        page_height: pageHeight,
      });
    }

    setIsSnipping(false);
    setSnippingBox(null);
    setHighlightedWords([]); // Clear highlighted words when done
  };

  if (!pdf || isLoading || !url) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        {isLoading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500 mx-auto mb-4"></div>
            <Text>Loading PDF...</Text>
          </div>
        ) : !url ? null : (
          <div className="text-center text-gray-500">{error ? <Text>{error}</Text> : <Text>No PDF loaded</Text>}</div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarTitle>Error</MessageBarTitle>
        <MessageBarBody>{error}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-40px)] flex flex-col" ref={containerRef}>
      <div
        className="mt-1 flex-grow overflow-auto relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: `${zoomLevel}%`,
            maxWidth: "none",
          }}
        />
        {highlightStyles}
        {isSnipping && snippingBox && <div style={snippingBoxStyle!} />}
        {highlightedWords.map((word, index) => (
          <div
            key={index}
            style={{
              position: "absolute",
              top: `${word.y}px`,
              left: `${word.x}px`,
              width: `${word.width}px`,
              height: `${word.height}px`,
              backgroundColor: "rgba(255, 255, 0, 0.3)",
              pointerEvents: "none",
              zIndex: 15,
            }}
          />
        ))}
      </div>

      <div className="flex items-center justify-between p-2 border-t h-16 flex-shrink-0 overflow-scroll">
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setZoomLevel((prev) => Math.max(25, prev - 25))}
            disabled={zoomLevel <= 25}
            icon={<span className="text-lg">-</span>}
          />
          <span className="w-20 text-center">{zoomLevel}%</span>
          <Button
            onClick={() => setZoomLevel((prev) => Math.min(400, prev + 25))}
            disabled={zoomLevel >= 400}
            icon={<span className="text-lg">+</span>}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Button onClick={() => changePage(-1)} disabled={pageNumber <= 1} icon={<ChevronLeft24Regular />} />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={pageNumber.toString()}
              onChange={(e) => setPageNumber(Math.max(1, Math.min(parseInt(e.target.value) || 1, numPages || 1)))}
              className="w-16 text-center"
            />
            <span>of {numPages || "unknown"}</span>
          </div>
          <Button
            onClick={() => changePage(1)}
            disabled={pageNumber >= (numPages || 1)}
            icon={<ChevronRight24Regular />}
          />
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
